import * as Axios from 'axios';
import BaseService from '@/services/common/BaseService';
import store from '@/store';

export interface IBankCategoryMapping {
    category: string;
    orgId: string;
}

export default class BankCategoryService extends BaseService {
    // Get API base URL - use admin-server for deployed envs, bankwatch for local dev
    private get apiBase(): string {
        const env = process.env.VUE_APP_ENV || 'dev';

        // Local dev: use bankwatch API directly (different DynamoDB instance)
        if (env === 'dev') {
            return 'http://localhost:3100';
        }

        // Deployed envs: use admin-server API (shared DynamoDB)
        return store.state.apiBase;
    }

    // API path varies by environment
    private get apiPath(): string {
        const env = process.env.VUE_APP_ENV || 'dev';

        // Local dev: bankwatch API structure
        if (env === 'dev') {
            return '/api/v1/admin/bank-categories';
        }

        // Deployed: admin-server API structure
        return '/bankwatch/bank-categories';
    }

    public async getAllCategories(): Promise<string[]> {
        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'get',
            url: `${this.apiBase}${this.apiPath}`,
            headers: { Authorization: `Bearer ${store.state.$auth.token}` }
        }

        const res: Axios.AxiosResponse = await this.makeAxiosCall(apiConfig);
        return res.data as string[];
    }

    public async getOrgsByCategory(category: string): Promise<string[]> {
        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'get',
            url: `${this.apiBase}${this.apiPath}/${category}/orgs`,
            headers: { Authorization: `Bearer ${store.state.$auth.token}` }
        }

        const res: Axios.AxiosResponse = await this.makeAxiosCall(apiConfig);
        return res.data as string[];
    }

    public async getCategoriesByOrg(orgId: string): Promise<string[]> {
        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'get',
            url: `${this.apiBase}${this.apiPath}/org/${orgId}`,
            headers: { Authorization: `Bearer ${store.state.$auth.token}` }
        }

        const res: Axios.AxiosResponse = await this.makeAxiosCall(apiConfig);
        return res.data as string[];
    }

    public async getAllMappings(): Promise<IBankCategoryMapping[]> {
        console.log('[BankCategoryService] Getting all mappings...');
        console.log('[BankCategoryService] API Base:', this.apiBase);
        console.log('[BankCategoryService] Auth token exists:', !!store.state.$auth?.token);

        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'get',
            url: `${this.apiBase}${this.apiPath}/all`,
            headers: { Authorization: `Bearer ${store.state.$auth.token}` }
        }

        console.log('[BankCategoryService] Making request to:', apiConfig.url);
        const res: Axios.AxiosResponse = await this.makeAxiosCall(apiConfig);
        console.log('[BankCategoryService] Response:', res.data);
        return res.data as IBankCategoryMapping[];
    }

    public async createMapping(category: string, orgId: string): Promise<any> {
        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'post',
            url: `${this.apiBase}${this.apiPath}`,
            data: { category, orgId },
            headers: { Authorization: `Bearer ${store.state.$auth.token}` }
        }

        const res: Axios.AxiosResponse = await this.makeAxiosCall(apiConfig);
        return res.data;
    }

    public async deleteMapping(category: string, orgId: string): Promise<void> {
        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'delete',
            url: `${this.apiBase}${this.apiPath}/${category}/${orgId}`,
            headers: { Authorization: `Bearer ${store.state.$auth.token}` }
        }

        const res: Axios.AxiosResponse = await this.makeAxiosCall(apiConfig);
        return res.data;
    }
}
